
import Vue from "vue";
import Component from "vue-class-component";
import TruckModule from "../store/modules/truck";
import TrucksActions from "@/components/truck/TrucksActions.vue";
import {getModule} from "vuex-module-decorators";
import {TruckOptimizationOrder} from "@/models/ui/truckOptimization";
import {
  ScheduleOverride,
  CalculateTimeframesResponse,
} from "@/models/api/trucks/timeframes";
import {TimeframeSchedulingSettings} from "@/models/api/system/timeframeSchedulingSettings";
import notifications from "../lib/notifications";
import {formatDateWithTime} from "../utils";
import moment from "moment";
import {SystemModule} from "@/store/modules";
import {Address} from "@/models/api/orders/get";
import {AccountModule} from "@/store/modules";
import Select from "../components/form/Select.vue";
import _ from "lodash";
import {Watch} from "vue-property-decorator";

const truckModuleState = getModule(TruckModule);
const systemModuleState = getModule(SystemModule);
const accountModuleState = getModule(AccountModule);

@Component({
  components: {
    TrucksActions,
    Select
  },
})
export default class OptimizeTruck extends Vue {
  startTime: string = "07:00";
  originType: string = "TimeFirstStop";
  originLocation: string = null;
  originLocations: Array<any> = [];
  loading: boolean = false;
  orders: TruckOptimizationOrder[] = null;
  saving: boolean = false;
  overrideTimeInformation: any = {
    order: null,
    hours: null,
    minutes: null,
    seconds: null,
    field: null,
    modalIsVisible: false,
  };
  overrideArrivalInformation: any = {
    order: null,
    value: null,
    originalValue: null,
    modalIsVisible: false,
  };
  overrideArrivalDateInformation: any = {
    value: null
  };
  newOriginAddress: any = {
    name: null,
    address1: null,
    address2: null,
    city: null,
    state: null,
    zipCode: null
  };
  finalizePromptModalIsVisible: boolean = false;
  acceptUnsuccessfulPromptModalIsVisible: boolean = false;
  addOriginModalIsVisible: boolean = false;
  hasPendingOverrides: boolean = false;
  response: CalculateTimeframesResponse = null;
  originAddressOverride: boolean = false;
  originAddress: Address = {
    streetLine1: null,
    streetLine2: null,
    city: null,
    state: null,
    zip: null,
  };
  timeframeSchedulingSettings: TimeframeSchedulingSettings = null;
  currentTruck: any = truckModuleState.currentTruck;

  @Watch("originType")
  async onChangedOriginType() {
    await this.saveUpdatedSetting();
  }

  @Watch("startTime")
  async onChangedStartTime(newValue: string, oldValue: string){
    await this.saveStartTimeSetting();
  }

  @Watch("originLocation")
  async onChangedOriginLocation() {
    await this.saveOriginSetting();
  }

  async saveNewOrigin() {
    const model = {
      name: this.newOriginAddress.name,
      address1: this.newOriginAddress.address1,
      address2: this.newOriginAddress.address2,
      city: this.newOriginAddress.city,
      state: this.newOriginAddress.state,
      zipCode: this.newOriginAddress.zipCode,
      truckId: this.truckId,
      facilityCode: truckModuleState.currentTruck.facilityCode
    }

    try {
      const result = await truckModuleState.addNewOrigin(model);
      truckModuleState.setCurrentTruck(result);

      this.originLocations = _.map(truckModuleState.currentTruck.facilityOrigins, x => {
        return {
          value: x.id,
          label: x.name
        }
      });

      notifications.success("Add New Origin", "New origin added");
    } catch {
      notifications.error("Add New Origin", "Error saving new origin");
    } finally {
      this.closeOriginModal();
    }
  }

  closeOriginModal() {
    this.addOriginModalIsVisible = false;
    this.newOriginAddress = {
      name: null,
      address1: null,
      address2: null,
      city: null,
      state: null,
      zipCode: null
    };
  }

  getOriginLocation() {
    if (!truckModuleState.currentTruck) return "";
    const foundTruck = _.find(truckModuleState.currentTruck.facilityOrigins, x => {
      return x.id == this.originLocation;
    });
    if (!foundTruck) return "";
    return foundTruck.address1 + ", " + foundTruck.city + ", " + foundTruck.state + " " + foundTruck.zipCode;
  }

  async calculate() {
    this.loading = true;

    const addressToSet = _.find(truckModuleState.currentTruck.facilityOrigins, x => {
      return x.id == this.originLocation;
    })

    if(addressToSet) {
      this.originAddress.streetLine1 = addressToSet.address1;
      this.originAddress.streetLine2 = addressToSet.address2;
      this.originAddress.city = addressToSet.city;
      this.originAddress.state = addressToSet.state;
      this.originAddress.zip = addressToSet.zipCode;
    }

    try {
      this.response = await truckModuleState.calculateTimeframes({
        truckId: this.truckId,
        earliestArrivalTime: this.startTime,
        truckDetailOverrides: this.truckOverrides,
        originAddress: this.originAddress,
        timeType: this.originType,
        originId: this.originLocation
      });

      this.hasPendingOverrides = false;
      this.orders = [];

      if (this.response.orderScheduleResults) {
        for (let index = 0; index < this.response.orderScheduleResults.length; index++) {
          this.orders.push(
            new TruckOptimizationOrder(this.response.orderScheduleResults[index])
          );
        }
      }
    } catch (ex) {
      notifications.error("Route Optimization", "Error generating routes", ex);
    }
    this.loading = false;
  }

  get truckId(): number {
    return +this.$route.params.idx;
  }

  get responseUnsuccessful(): boolean {
    return this.response != null && this.response.status && this.response.status.success === false;
  }

  get totalMiles(): number {
    if (!this.response || !this.response.returnTravelMiles)
      return 0;

    return this.response.returnTravelMiles + this.response.orderScheduleResults.reduce((a, b) => a + (b['travelMiles'] || 0), 0);
  }

  overrideTime(order: TruckOptimizationOrder, field: string) {
    let value = order[field];

    if (value) {
      const data = value.split(":");

      this.overrideTimeInformation.hours = data[0];
      this.overrideTimeInformation.minutes = data[1];
      this.overrideTimeInformation.seconds = data[2];
    }
    this.overrideTimeInformation.order = order;
    this.overrideTimeInformation.field = field;
    this.overrideTimeInformation.modalIsVisible = true;
  }

  overrideArrival(order: TruckOptimizationOrder) {
    this.overrideArrivalInformation.order = order;
    if (order.anticipatedArrival)
      this.overrideArrivalInformation.value = order.anticipatedArrival;
    else this.overrideArrivalInformation.value = order.estimatedArrival;

    this.overrideArrivalInformation.originalValue = order.estimatedArrival;
    this.overrideArrivalInformation.modalIsVisible = true;
  }

  formatDate(date: Date) {
    return formatDateWithTime(date);
  }

  processArrivalOverride(event) {
    this.hasPendingOverrides = true;
    const originalInfo = this.overrideArrivalInformation.originalValue.split("T");
    const existingTimeWithDate = moment(this.overrideArrivalInformation.originalValue);
    const newTimeDate = this.overrideArrivalDateInformation.value ? this.overrideArrivalDateInformation.value : originalInfo[0];
    const newTimeTime = this.overrideArrivalInformation.value.split("T").length === 1 ? this.overrideArrivalInformation.value.split("T")[0] : originalInfo[1];
    const newTimeWithDate = moment(newTimeDate + "T" + newTimeTime);

    if (!this.overrideArrivalInformation.value) {
      notifications.error("Invalid time", "New time is required");
      event.preventDefault();
      return;
    }
    if (newTimeWithDate < existingTimeWithDate) {
      notifications.error(
        "Invalid time",
        "New arrival date has to be after the original date"
      );
      event.preventDefault();
      return;
    }

    this.overrideArrivalInformation.order.anticipatedArrival =
      newTimeWithDate.format();
    this.overrideArrivalInformation.order.anticipatedArrivalOverride = true;
  }

  isOverrideTimeInvalid(): boolean {
    if (this.overrideTimeInformation.hours === '' || this.overrideTimeInformation.minutes === '' || this.overrideTimeInformation.seconds === '') {
      return true;
    }

    if (this.overrideTimeInformation.hours < 0 || this.overrideTimeInformation.hours > 12) {
      return true;
    }

    if (this.overrideTimeInformation.minutes < 0 || this.overrideTimeInformation.minutes > 59) {
      return true;
    }

    if (this.overrideTimeInformation.seconds < 0 || this.overrideTimeInformation.seconds > 59) {
      return true;
    }

    return false;
  }

  processOverride() {
    this.overrideTimeInformation.modalIsVisible = false;
    this.hasPendingOverrides = true;
    if (
      !this.overrideTimeInformation.order[
      this.overrideTimeInformation.field + "Original"
        ] ||
      this.overrideTimeInformation.order[
      this.overrideTimeInformation.field + "Original"
        ] == ""
    ) {
      this.overrideTimeInformation.order[
      this.overrideTimeInformation.field + "Original"
        ] =
        this.overrideTimeInformation.order[this.overrideTimeInformation.field];
    }

    this.overrideTimeInformation.order[
      this.overrideTimeInformation.field
      ] = `${this.overrideTimeInformation.hours}:${this.overrideTimeInformation.minutes}:${this.overrideTimeInformation.seconds}`;
    this.overrideTimeInformation.order[
    this.overrideTimeInformation.field + "Override"
      ] = true;
  }

  get truckOverrides(): ScheduleOverride[] {
    if (!this.orders || this.orders.length < 1) return null;

    return this.orders
      .filter(
        (x) =>
          x.overheadTimeOverride ||
          x.travelTimeOverride ||
          x.unloadTimeOverride ||
          x.anticipatedArrivalOverride
      )
      .map((x) => ({
        truckDetailId: x.truckDetailId,
        anticipatedArrivalDateTime: x.anticipatedArrivalOverride
          ? x.anticipatedArrival
          : null,
        overrideAddress: null,
        overrideTravelTime: x.travelTimeOverride ? x.travelTime : null,
        overrideOverheadTime: x.overheadTimeOverride ? x.overheadTime : null,
        overrideUnloadTime: x.unloadTimeOverride ? x.unloadTime : null,
      }));
  }

  undoOverride(order: TruckOptimizationOrder, field: string) {
    order[field] = order[`${field}Original`];
    order[`${field}Override`] = false;
    this.hasPendingOverrides = true;
  }

  get canCommitRoutes() {
    return !this.hasPendingOverrides && this.response;
  }

  async commitRoutes(acceptUnsuccessful: boolean) {
    if (!this.canCommitRoutes) return;

    this.saving = true;

    this.response.acceptUnsuccessful = acceptUnsuccessful;

    if (!acceptUnsuccessful && this.responseUnsuccessful) {
      this.acceptUnsuccessfulPromptModalIsVisible = true;
    } else {
      try {
        const response = await truckModuleState.commitTimeframes(this.response);
        notifications.success(
          "Commit Timelines",
          "Timelines successfully saved"
        );
        this.finalizePromptModalIsVisible = true;
      } catch (ex) {
        notifications.error("Commit Timelines", "Failed to save timelines", ex);
      }
    }

    this.saving = false;
  }

  finalizeTruck() {
    this.finalizePromptModalIsVisible = false;
    truckModuleState.finalizeTruckShortcutAction({
      selectedItems: [truckModuleState.currentTruck],
    });
  }

  async created() {
    try {
      await truckModuleState.loadTruck(+this.truckId);
      this.timeframeSchedulingSettings =
        await systemModuleState.getTimeframeSchedulingSettings({
          facilityCode: truckModuleState.currentTruck.facilityCode,
        });
      this.startTime = this.timeframeSchedulingSettings.defaultStartTime;

      let originAddress = await systemModuleState.getTimeframeOriginAddress(truckModuleState.currentTruck.facilityCode);
      if (originAddress) {
        this.originAddressOverride = originAddress.specifyOriginAddress;
        this.originAddress = originAddress.address;
      }

      this.originLocations = this.originLocations.concat(_.map(truckModuleState.currentTruck.facilityOrigins, (x => {
        return {
          label: x.name,
          value: x.id
        }
      })));


    } catch (ex) {
      await this.$router.push(`/trucks`);
    }

    await this.loadUserSettings()
    await this.loadUserStartTime();
    await this.loadUserOrigin();
  }

  async loadUserOrigin() {
    await accountModuleState.loadUserSettings("timeFrameOrigin");

    if(accountModuleState.timeFrameOriginUserSettings && accountModuleState.timeFrameOriginUserSettings.length > 0){
      const currentTimeFrameOrigin = accountModuleState.timeFrameOriginUserSettings[0];
      const item = _.find(this.originLocations, (x) => {
        return x.value == currentTimeFrameOrigin;
      })

      if(!item) {
        this.originLocation = this.originLocations[0].value;
      } else {
        this.originLocation = item.value;
      }
    } else {
      this.originLocation = this.originLocations[0].value;
    }
  }

  async saveOriginSetting() {
    await accountModuleState.updateUserSettings({
      settingName: "timeFrameOrigin",
      values: [this.originLocation.toString()]
    })
  }

  async loadUserStartTime() {
    await accountModuleState.loadUserSettings("timeFrameStart");
    if(accountModuleState.timeFrameStartUserSettings && accountModuleState.timeFrameStartUserSettings.length > 0){
      const currentTimeFrameStart = accountModuleState.timeFrameStartUserSettings[0]
      this.startTime = currentTimeFrameStart;
    }
  }

  async saveStartTimeSetting() {
    await accountModuleState.updateUserSettings({
      settingName: "timeFrameStart",
      values: [this.startTime]
    })
  }

  async saveUpdatedSetting(){
    await accountModuleState.updateUserSettings({
      settingName: "timeFrameType",
      values: [this.originType]
    })
  }

  async loadUserSettings() {
    await accountModuleState.loadUserSettings("timeFrameType");

    if(accountModuleState.timeFrameTypeUserSettings && accountModuleState.timeFrameTypeUserSettings.length > 0){
      const currentTimeFrameType = accountModuleState.timeFrameTypeUserSettings[0]
      if(currentTimeFrameType && currentTimeFrameType.length > 0){
        this.originType = currentTimeFrameType
      }
    }
  }
}
